import React, { useState, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { X, Info, Upload, FileText } from 'lucide-react'

interface VendorResponse {
  vendorName: string
  files: File[]
}

interface UploadVendorResponsesModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (responses: VendorResponse[]) => void
}

export const UploadVendorResponsesModal: React.FC<UploadVendorResponsesModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [vendors, setVendors] = useState<VendorResponse[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAddingMode, setIsAddingMode] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const items = Array.from(e.dataTransfer.items)
    processDroppedItems(items)
  }

  const processDroppedItems = async (items: DataTransferItem[]) => {
    const newVendors: VendorResponse[] = []
    const promises: Promise<void>[] = []

    for (const item of items) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry()
        if (entry) {
          if (entry.isDirectory) {
            // Process folder - create a promise for each folder
            const promise = readDirectory(entry as FileSystemDirectoryEntry).then(files => {
              if (files.length > 0) {
                newVendors.push({
                  vendorName: entry.name,
                  files: files
                })
              }
            })
            promises.push(promise)
          } else if (entry.isFile) {
            // Skip individual files - only folders are supported
            console.warn('Individual files are not supported. Please organize files into vendor folders.')
          }
        }
      }
    }

    // Wait for all folder processing to complete
    await Promise.all(promises)
    
    // Show warning if no valid vendor folders were found
    if (newVendors.length === 0) {
      console.warn('No valid vendor folders found. Please make sure to drag folders (not individual files) containing vendor proposal documents.')
      return
    }
    
    // Merge with existing vendors (replace duplicates)
    const updatedVendors = [...vendors]
    let addedCount = 0
    let duplicateCount = 0
    
    newVendors.forEach(newVendor => {
      const existingIndex = updatedVendors.findIndex(v => v.vendorName.toLowerCase() === newVendor.vendorName.toLowerCase())
      
      if (existingIndex >= 0) {
        // Duplicate found - replace the existing vendor
        updatedVendors[existingIndex] = newVendor
        duplicateCount++
      } else {
        // New vendor - add to list
        updatedVendors.push(newVendor)
        addedCount++
      }
    })
    
    setVendors(updatedVendors)
    setIsAddingMode(true)
    
    // Provide detailed user feedback
    console.log('🎯 VENDOR UPLOAD SUMMARY:')
    newVendors.forEach(vendor => {
      console.log(`📂 ${vendor.vendorName}: ${vendor.files.length} files`)
      vendor.files.forEach(file => {
        console.log(`  📄 ${file.name} (${(file.size / 1024).toFixed(1)} KB)`)
      })
    })

    if (duplicateCount > 0) {
      console.log(`✅ Added ${addedCount} new vendor(s) and replaced ${duplicateCount} existing vendor(s)`)
    } else {
      console.log(`✅ Added ${addedCount} vendor(s) to selection`)
    }

    const totalFiles = updatedVendors.reduce((sum, vendor) => sum + vendor.files.length, 0)
    console.log(`📊 Total: ${updatedVendors.length} vendors, ${totalFiles} files`)
  }

  const readDirectory = async (directoryEntry: FileSystemDirectoryEntry): Promise<File[]> => {
    const files: File[] = []
    const dirReader = directoryEntry.createReader()

    // Read all entries (may require multiple calls due to browser batching)
    let allEntries: FileSystemEntry[] = []
    let batch: FileSystemEntry[]

    console.log(`📁 Reading directory: ${directoryEntry.name}`)

    do {
      batch = await new Promise<FileSystemEntry[]>((resolve) => {
        dirReader.readEntries((entries) => {
          console.log(`📄 Read batch of ${entries.length} entries`)
          resolve(entries)
        })
      })
      allEntries = allEntries.concat(batch)
    } while (batch.length > 0)

    console.log(`📄 Total entries found: ${allEntries.length}`)

    // Process all file entries
    for (const entry of allEntries) {
      if (entry.isFile) {
        try {
          const file = await new Promise<File>((resolve, reject) => {
            (entry as FileSystemFileEntry).file((f) => {
              console.log(`📄 Processing file: ${f.name} (${f.type})`)
              resolve(f)
            }, reject)
          })

          // Accept all common document and image types based on file extension
          const allowedExtensions = [
            '.pdf', '.doc', '.docx', '.txt', '.rtf',  // Documents
            '.xls', '.xlsx', '.xlsm', '.csv',          // Spreadsheets
            '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tif', '.tiff',   // Images
            '.zip', '.rar'                             // Archives
          ]

          const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

          if (allowedExtensions.includes(fileExt)) {
            files.push(file)
            console.log(`✅ Added file: ${file.name} (${fileExt})`)
          } else {
            console.log(`❌ Skipped file: ${file.name} (${fileExt}) - not in allowed types`)
          }
        } catch (error) {
          console.error(`❌ Error reading file ${entry.name}:`, error)
        }
      }
    }

    console.log(`📁 Directory ${directoryEntry.name} processed: ${files.length} valid files`)
    return files
  }


  const handleSubmit = async () => {
    if (vendors.length > 0) {
      setIsLoading(true)
      try {
        await onSubmit(vendors)
        setVendors([])
        onClose()
      } finally {
        setIsLoading(false)
      }
    }
  }

  const clearAllVendors = () => {
    setVendors([])
    setIsAddingMode(false)
  }


  const getFolderCountText = () => {
    if (vendors.length === 0) return ''
    return vendors.length === 1 ? '1 folder selected' : `${vendors.length} folders selected`
  }

  const handleModalClose = () => {
    setVendors([])
    setIsAddingMode(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} title="">
      <div className="bg-white rounded-lg w-full max-w-[624px] relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <h2 className="text-[18px] font-semibold text-[#1b1d1e] leading-normal">Upload vendor proposals</h2>
          <button
            onClick={handleModalClose}
            className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-5">
          {/* Info Message */}
          <div className="flex items-start gap-2 mb-4">
            <Info className="w-4 h-4 text-[#7e8c8d] mt-0.5 flex-shrink-0" />
            <p className="text-[12px] font-normal text-[#7e8c8d] leading-normal w-[539px]">
              Drag and drop vendor folders here. Each folder should be named after the vendor and contain their proposal files. 
              Only folders are accepted - individual files will be ignored.
            </p>
          </div>

          {/* Folder Count and Clear All */}
          {vendors.length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-700">
                {getFolderCountText()}
              </span>
              <button
                onClick={clearAllVendors}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
                disabled={isLoading}
              >
                Clear All
              </button>
            </div>
          )}

          {/* Drop Zone */}
          <div
            className={`bg-[#f6f8f9] border border-dashed border-[#7e8c8d] rounded-lg h-[262px] flex flex-col items-center justify-center relative transition-colors cursor-pointer ${
              isDragOver
                ? 'border-blue-500 bg-blue-50'
                : ''
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-7 w-[540px]">
              {/* Upload Icon */}
              <div className="w-[47.225px] h-[47.919px] flex items-center justify-center">
                <div className="relative">
                  <FileText className="w-8 h-8 text-[#7e8c8d]" />
                  <Upload className="w-4 h-4 text-[#7e8c8d] absolute -top-1 -right-1" />
                </div>
              </div>
              
              {/* Upload Text */}
              <div className="flex flex-col items-center gap-[15px] w-full">
                <div className="flex items-center justify-center text-center text-[#292d32]">
                  <span className="text-[14px] font-normal">Drag & drop vendor folders here</span>
                </div>
              </div>
            </div>
          </div>

        {/* Vendor List */}
        {vendors.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              Detected Vendors ({vendors.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {vendors.map((vendor, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{vendor.vendorName}</p>
                    <p className="text-sm text-gray-600">{vendor.files.length} file(s)</p>
                  </div>
                  <button
                    onClick={() => setVendors(vendors.filter((_, i) => i !== index))}
                    className="text-red-500 hover:text-red-700"
                    disabled={isLoading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

          {/* Footer */}
          <div className="flex justify-end gap-4 pt-4 mt-8 border-t border-gray-100">
            <Button
              onClick={handleSubmit}
              disabled={vendors.length === 0 || isLoading}
              className={`h-9 px-4 py-2 rounded text-[14px] font-semibold ${
                vendors.length === 0 || isLoading 
                  ? 'bg-[#ccd1d1] text-[#f6f8f9] cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {isLoading ? 'Importing...' : 'Import'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}